import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { withErrorBoundary } from '@/utils/errors';
import styles from './VersionUpdateBanner.module.scss';

// const POLL_INTERVAL_MS = 15 * 1000; // 15 seconds
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const DISMISSED_HASH_KEY = 'dismissed_branch_hash';

const fetchBranchHash = async () => {
  try {
    const res = await fetch('/v1/branch_info');
    if (!res.ok) return null;
    const data = await res.json();
    return data.branch_hash;
  } catch (err) {
    return null;
  }
};

const VersionUpdateBanner = () => {
  const [visible, setVisible] = useState(false);
  const baselineHash = useRef(null);

  useEffect(() => {
    // Deploys roll out pod-by-pod behind a load balancer, so a poll can transiently
    // hit an old pod even after new ones are up. Once we've seen a hash other than
    // the baseline we latch `visible` true and never flip it back from a poll result
    // (only Dismiss hides it) — otherwise the banner would flicker during rollout.
    let intervalId;

    const flagIfNotDismissed = hash => {
      // Record the hash as soon as we decide to show it, not only when Dismiss/Reload
      // is clicked — a plain browser refresh bypasses those handlers entirely, and
      // without this it would reload straight into the same prompt.
      const dismissedHash = window.localStorage.getItem(DISMISSED_HASH_KEY);
      if (hash === dismissedHash) return;
      window.localStorage.setItem(DISMISSED_HASH_KEY, hash);
      setVisible(true);
    };

    const checkForUpdate = async () => {
      const hash = await fetchBranchHash();
      if (hash === null) return;

      if (baselineHash.current === null) {
        baselineHash.current = hash;
        return;
      }

      if (hash !== baselineHash.current) flagIfNotDismissed(hash);
    };

    const restartInterval = () => {
      clearInterval(intervalId);
      intervalId = setInterval(checkForUpdate, POLL_INTERVAL_MS);
    };

    checkForUpdate();
    restartInterval();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForUpdate();
        restartInterval();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleDismiss = () => setVisible(false);

  const handleReload = () => window.location.reload();

  if (!visible) return null;

  return (
    <div className={styles.banner}>
      <button onClick={handleDismiss} className={styles.close} aria-label="Dismiss">
        <X size={14} />
      </button>
      <button onClick={handleReload} className={styles.reload} aria-label="Reload the page">
        <RefreshCw size={26} />
      </button>
      <div className={styles.content}>
        <p className={styles.title}>A new version of the app is available.</p>
        <p className={styles.subtitle}>Save your changes before reloading.</p>
      </div>
    </div>
  );
};

export default withErrorBoundary(VersionUpdateBanner);
